// src/components/video/uploadedVideoFrame.js
//
// Draws one frame of a "My video" — a video YOU uploaded — with:
//
//   * your video filling the frame (letterboxed if it isn't 16:9),
//   * AI / uploaded pictures shown over it between the times you pick
//     ("from 0:12 to 0:18, show the water-cycle picture"),
//   * the song's lyrics along the bottom, lighting up word by word, in the
//     same dark bar the Lesson video studio uses,
//   * the SBL logo in the corner (optional),
//   * SHAPES on top of your video — rectangles, rounded boxes and circles in
//     a colour you pick (or a blur of the video under them), to cover text
//     or anything else already in your video.
//
// LAYERS, bottom to top: your video → shapes → pictures → lyrics (sync
// text) → logo. The sync text is always drawn last, so it stays readable
// over any shape or picture.
//
// It is the Video upload studio's renderer ONLY. The Lesson video studio's
// own renderer (lessonVideo.js) is not used or changed by this file, so
// nothing here can move anything in an animated lesson video.
//
// TIMING
// ------
// The lyrics follow the song's sing-along timings (lyricSync/{slug}), which
// are measured from the start of the SONG FILE. Your video's song may start
// a few seconds in, so `offset` is "the video time where the song's 0:00
// is". The lyrics at video time t are the lyrics at song time t − offset.
//
// Word highlighting uses frameAt() from lyricSyncService — the same function
// the sing-along player and its MP4 export use — so a line you timed with
// Tap by word lights up word-for-word here too.
//
// Pure canvas, no React or Firebase. The studio preview and the MP4 export
// call the same renderUploadedFrame() every frame.

import { frameAt, wordsOf } from "../../services/lyricSyncService";

const INK = "#2a2130";
const FONT = '"Baloo 2", "Fredoka", "Arial Rounded MT Bold", "Trebuchet MS", sans-serif';
const clamp01 = (x) => Math.max(0, Math.min(1, x));
const smooth = (x) => {
  const v = clamp01(x);
  return v * v * (3 - 2 * v);
};

/** How long a picture takes to fade in and out, seconds. */
export const PICTURE_FADE = 0.4;

/** Picture layouts offered in the studio. */
export const PICTURE_FITS = [
  { key: "board", label: "Big screen in the middle" },
  { key: "full", label: "Full screen" },
  { key: "corner", label: "Small, top corner" },
  { key: "left", label: "Left half" },
  { key: "right", label: "Right half" },
  { key: "custom", label: "Anywhere — drag it on the preview" },
];

/** Shape kinds offered in the studio. */
export const SHAPE_TYPES = [
  { key: "rect", label: "Rectangle", icon: "▭" },
  { key: "round", label: "Rounded box", icon: "▢" },
  { key: "ellipse", label: "Circle / oval", icon: "◯" },
];

/** Is a shape on screen at video time t? */
export const shapeActiveAt = (shape, t) =>
  shape.always !== false || (Number(t) >= Number(shape.from) && Number(t) < Number(shape.to));

/** Is a picture clip on screen at video time t? */
export const clipActiveAt = (clip, t) => Number(t) >= Number(clip.from) && Number(t) < Number(clip.to);

/* ------------------------------------------------------------------ */
/* Pictures: load once, draw every frame                               */
/* ------------------------------------------------------------------ */
//
// crossOrigin="anonymous" is required: a canvas that draws a picture from
// another site without CORS can no longer be recorded. Pictures in SBL
// storage pass, because the bucket's CORS lists the portal.

const pictures = new Map(); // url -> { img, state, promise }
const listeners = new Set();

/** Called whenever a picture finishes loading (or fails), to redraw. */
export const onOverlayPictureLoaded = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

const loadPicture = (url) => {
  if (!url) return null;
  const hit = pictures.get(url);
  if (hit) return hit;
  const entry = { img: new Image(), state: "loading" };
  entry.img.crossOrigin = "anonymous";
  entry.img.decoding = "async";
  entry.promise = new Promise((resolve) => {
    entry.img.onload = () => {
      entry.state = "ok";
      listeners.forEach((fn) => fn(url));
      resolve(true);
    };
    entry.img.onerror = () => {
      entry.state = "bad";
      listeners.forEach((fn) => fn(url));
      resolve(false);
    };
  });
  entry.img.src = url;
  pictures.set(url, entry);
  return entry;
};

/** Load every picture before recording. Resolves { ok, failed: [url] }. */
export const preloadOverlayPictures = async (urls, timeoutMs = 20000) => {
  const list = Array.from(new Set((urls || []).filter(Boolean)));
  const results = await Promise.all(
    list.map((u) =>
      Promise.race([loadPicture(u).promise, new Promise((r) => setTimeout(() => r(false), timeoutMs))])
    )
  );
  const failed = list.filter((u, n) => !results[n]);
  return { ok: failed.length === 0, failed };
};

/** Picture state for the studio's thumbnails: "loading" | "ok" | "bad". */
export const overlayPictureState = (url) => pictures.get(url)?.state || (url ? "loading" : "none");

/* ------------------------------------------------------------------ */
/* Drawing helpers                                                     */
/* ------------------------------------------------------------------ */

/** Fit (letterbox) a source of size sw×sh into the box. */
const containRect = (sw, sh, x, y, w, h) => {
  const s = Math.min(w / sw, h / sh);
  const dw = sw * s;
  const dh = sh * s;
  return { x: x + (w - dw) / 2, y: y + (h - dh) / 2, w: dw, h: dh };
};

/** Fill the box with the image, cropping the overflow; z = gentle zoom. */
const drawCover = (ctx, img, x, y, w, h, z = 1) => {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) return;
  const s = Math.max(w / iw, h / ih) * Math.max(1, z);
  const dw = iw * s;
  const dh = ih * s;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
};

const pill = (ctx, x, y, text, size, bg, fg, alpha = 1) => {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = `800 ${size}px ${FONT}`;
  const w = ctx.measureText(text).width + size * 1.1;
  const h = size * 1.55;
  ctx.beginPath();
  ctx.roundRect(x - w / 2, y - h / 2, w, h, h / 2);
  ctx.fillStyle = bg;
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = INK;
  ctx.stroke();
  ctx.fillStyle = fg;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x, y + size * 0.06);
  ctx.restore();
};

/** Where a picture goes for each layout, on a W×H frame. */
const rectFor = (fit, W, H, barH) => {
  const usable = H - barH;
  switch (fit) {
    case "full":
      return { x: 0, y: 0, w: W, h: H, framed: false };
    case "corner": {
      const w = W * 0.34;
      const h = (w * 9) / 16;
      return { x: W - w - W * 0.025, y: H * 0.04, w, h, framed: true };
    }
    case "left":
      return { x: 0, y: 0, w: W / 2, h: usable, framed: false };
    case "right":
      return { x: W / 2, y: 0, w: W / 2, h: usable, framed: false };
    case "board":
    default: {
      const w = W * 0.64;
      const h = Math.min(usable * 0.86, (w * 9) / 16);
      return { x: (W - w) / 2, y: Math.max(H * 0.035, (usable - h) / 2), w, h, framed: true };
    }
  }
};

/** One picture clip, faded in and out at its ends. */
const drawClip = (ctx, clip, t, W, H, barH) => {
  const from = Number(clip.from);
  const to = Number(clip.to);
  if (!(to > from) || t < from || t >= to || !clip.url) return;
  const local = t - from;
  const span = to - from;
  const fade = Math.min(PICTURE_FADE, span / 3);
  const alpha = smooth(local / fade) * smooth((to - t) / fade);
  if (alpha <= 0) return;

  const r =
    clip.fit === "custom"
      ? { x: Number(clip.x) * W, y: Number(clip.y) * H, w: Number(clip.w) * W, h: Number(clip.h) * H, framed: Boolean(clip.frame) }
      : rectFor(clip.fit, W, H, barH);
  if (!(r.w > 1 && r.h > 1)) return;
  const pic = loadPicture(clip.url);
  // a slow push-in so a still picture doesn't look frozen
  const zoom = clip.still ? 1 : 1 + 0.05 * clamp01(local / Math.max(1, span));

  ctx.save();
  ctx.globalAlpha = alpha;
  if (r.framed) {
    const bez = Math.max(6, W * 0.008);
    ctx.fillStyle = "rgba(0,0,0,0.25)";
    ctx.beginPath();
    ctx.roundRect(r.x + 6, r.y + 10, r.w, r.h, 24);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.roundRect(r.x - bez, r.y - bez, r.w + bez * 2, r.h + bez * 2, 26);
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = INK;
    ctx.stroke();
    ctx.beginPath();
    ctx.roundRect(r.x, r.y, r.w, r.h, 18);
    ctx.clip();
  } else {
    ctx.beginPath();
    ctx.rect(r.x, r.y, r.w, r.h);
    ctx.clip();
  }
  if (pic?.state === "ok") {
    drawCover(ctx, pic.img, r.x, r.y, r.w, r.h, zoom);
  } else {
    ctx.fillStyle = "rgba(90,63,208,0.22)";
    ctx.fillRect(r.x, r.y, r.w, r.h);
  }
  ctx.restore();

  // exact words / numbers over the picture — AI pictures don't draw these
  const caption = String(clip.caption || "").trim();
  if (caption) {
    const size = Math.round(H * (clip.fit === "corner" ? 0.04 : 0.058));
    const y = clip.fit === "full" ? H * 0.12 : r.y + r.h - size * 0.95;
    pill(ctx, r.x + r.w / 2, y, caption, size, "#ffffff", "#5a3fd0", alpha);
  }
};

/* ------------------------------------------------------------------ */
/* Shapes (cover-ups and drawings)                                     */
/* ------------------------------------------------------------------ */

const shapePath = (ctx, kind, x, y, w, h) => {
  ctx.beginPath();
  if (kind === "ellipse") ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
  else if (kind === "round") ctx.roundRect(x, y, w, h, Math.min(w, h) * 0.22);
  else ctx.rect(x, y, w, h);
};

/**
 * One shape. "color" fills it (opacity 0–1). "blur" redraws the video
 * under it blurred, which hides text even on a busy background. Shapes snap
 * on and off (no fade) so a cover-up never lets the text underneath flash.
 */
const drawShape = (ctx, shape, W, H, video, vr) => {
  const x = Number(shape.x) * W;
  const y = Number(shape.y) * H;
  const w = Number(shape.w) * W;
  const h = Number(shape.h) * H;
  if (!(w > 0.5 && h > 0.5)) return;
  ctx.save();
  shapePath(ctx, shape.kind, x, y, w, h);
  if (shape.mode === "blur" && video && vr) {
    ctx.clip();
    ctx.filter = `blur(${Math.max(6, Math.round(W * 0.012))}px)`;
    try {
      ctx.drawImage(video, vr.x, vr.y, vr.w, vr.h);
    } catch {
      /* frame not ready */
    }
    ctx.filter = "none";
  } else {
    ctx.globalAlpha = Math.max(0, Math.min(1, Number(shape.opacity ?? 1)));
    ctx.fillStyle = shape.fill || "#000000";
    ctx.fill();
  }
  ctx.restore();
};

/* ------------------------------------------------------------------ */
/* Lyrics along the bottom                                             */
/* ------------------------------------------------------------------ */
//
// Same look as the Lesson video studio's bar: a dark band, a yellow top
// line, the section name in a pill on the left, and the line in the middle
// turning yellow word by word.

const fitCache = new Map();

/** What the bar shows at song time `st`: { text, lit, label, alpha } or null. */
export const lyricBarAt = (parsed, cues, st) => {
  if (!parsed || !cues || !cues.length) return null;
  const f = frameAt(parsed, cues, st);
  if (f.mode === "sing") {
    const i = f.activeLine;
    return { text: parsed.lines[i], lit: f.litWords || 0, label: f.page?.label || "", alpha: 1 };
  }
  if (f.mode === "intro" || f.mode === "break") {
    // the next line, waiting, a few seconds before it is sung
    const cd = Number(f.countdown);
    if (!Number.isFinite(cd) || cd > 4) return null;
    const next = cues.find((c) => c.t > st);
    if (!next) return null;
    return {
      text: parsed.lines[next.i],
      lit: 0,
      label: parsed.pages[parsed.pageOfLine[next.i]]?.label || "",
      alpha: clamp01((4 - cd) / 0.4),
    };
  }
  return null;
};

const drawLyricBar = (ctx, W, H, bar) => {
  const barH = H * 0.14;
  const y0 = H - barH;
  ctx.save();
  ctx.globalAlpha = bar.alpha;
  ctx.fillStyle = "rgba(24,18,44,0.82)";
  ctx.fillRect(0, y0, W, barH);
  ctx.fillStyle = "#ffd23f";
  ctx.fillRect(0, y0, W, 4);

  const text = String(bar.text || "");
  const words = wordsOf(text);
  let left = 24;
  if (bar.label) {
    ctx.font = `800 ${Math.round(H * 0.028)}px ${FONT}`;
    const w = ctx.measureText(bar.label).width + 26;
    const ph = H * 0.047;
    ctx.beginPath();
    ctx.roundRect(left, y0 + barH / 2 - ph / 2, w, ph, ph / 2);
    ctx.fillStyle = "rgba(255,255,255,0.16)";
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(bar.label, left + w / 2, y0 + barH / 2 + 1);
    left += w + 16;
  }

  const room = W - left - 40;
  const key = `${text}|${room}|${barH}`;
  let size = fitCache.get(key);
  if (!size) {
    size = Math.round(barH * 0.4);
    ctx.font = `800 ${size}px ${FONT}`;
    while (ctx.measureText(text).width > room && size > 18) {
      size -= 2;
      ctx.font = `800 ${size}px ${FONT}`;
    }
    if (fitCache.size > 500) fitCache.clear();
    fitCache.set(key, size);
  }
  ctx.font = `800 ${size}px ${FONT}`;
  const space = ctx.measureText(" ").width;
  const widths = words.map((w) => ctx.measureText(w).width);
  const lineW = widths.reduce((a, b) => a + b, 0) + space * Math.max(0, words.length - 1);
  let x = Math.max(left, (W - lineW) / 2);
  const y = y0 + barH / 2 + 2;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  words.forEach((w, idx) => {
    ctx.lineWidth = 5;
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.strokeText(w, x, y);
    ctx.fillStyle = idx < bar.lit ? "#ffd23f" : "#ffffff";
    ctx.fillText(w, x, y);
    x += widths[idx] + space;
  });
  ctx.restore();
};

/* ------------------------------------------------------------------ */
/* One frame                                                           */
/* ------------------------------------------------------------------ */

/**
 * opts: {
 *   W, H,           frame size to draw (the canvas, or a scaled base frame)
 *   t,              VIDEO time in seconds
 *   video,          the <video> element (drawn as the background)
 *   offset = 0,     video time where the song's 0:00 is
 *   parsed, cues,   parseLyrics(lines) and the sing-along cues
 *   showLyrics = true,
 *   overlays = [],  [{ from, to, url, fit, caption, still, x, y, w, h, frame }]
 *   shapes = [],    [{ kind, x, y, w, h, fill, opacity, mode, always, from, to }]
 *   logo = null,    SBL logo <img> (crossOrigin) for the corner
 * }
 */
export const renderUploadedFrame = (ctx, opts) => {
  const {
    W,
    H,
    t,
    video,
    offset = 0,
    parsed,
    cues,
    showLyrics = true,
    overlays = [],
    shapes = [],
    logo = null,
  } = opts;

  ctx.save();
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, W, H);

  // your video, letterboxed
  let vr = null;
  if (video && video.readyState >= 2 && video.videoWidth) {
    vr = containRect(video.videoWidth, video.videoHeight, 0, 0, W, H);
    try {
      ctx.drawImage(video, vr.x, vr.y, vr.w, vr.h);
    } catch {
      /* a frame that can't be drawn yet — black for one frame */
    }
  }

  // shapes straight on the video, so they cover what is IN the video
  (shapes || []).forEach((shape) => {
    if (shapeActiveAt(shape, t)) drawShape(ctx, shape, W, H, vr ? video : null, vr);
  });

  const bar = showLyrics ? lyricBarAt(parsed, cues, t - (Number(offset) || 0)) : null;
  const barH = bar ? H * 0.14 : 0;

  // pictures, in time order (a later one sits on top of an earlier one)
  [...(overlays || [])]
    .sort((a, b) => Number(a.from) - Number(b.from))
    .forEach((clip) => drawClip(ctx, clip, t, W, H, barH));

  // sync text last of all, so it is always on top of shapes and pictures
  if (bar) drawLyricBar(ctx, W, H, bar);

  if (logo && logo.naturalWidth) {
    const lw = W * 0.07;
    const lh = (lw * logo.naturalHeight) / logo.naturalWidth;
    ctx.globalAlpha = 0.9;
    ctx.drawImage(logo, 18, 14, lw, lh);
    ctx.globalAlpha = 1;
  }
  ctx.restore();
};

/**
 * The colour of your VIDEO (not the shapes or pictures over it) at a point
 * of the frame, as "#rrggbb" — for matching a cover-up to the background.
 * fx/fy are 0–1 across/down the frame. Averages a small square so one odd
 * pixel doesn't decide. Throws if the browser won't let the page read the
 * video's pixels (bucket CORS).
 */
export const sampleVideoColor = (video, fx, fy, W = 1280, H = 720) => {
  if (!video || !video.videoWidth) throw new Error("Load your video first.");
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  const vr = containRect(video.videoWidth, video.videoHeight, 0, 0, W, H);
  ctx.drawImage(video, vr.x, vr.y, vr.w, vr.h);
  const r = 3;
  const px = Math.max(0, Math.min(W - 1, Math.round(fx * W)));
  const py = Math.max(0, Math.min(H - 1, Math.round(fy * H)));
  const d = ctx.getImageData(Math.max(0, px - r), Math.max(0, py - r), r * 2 + 1, r * 2 + 1).data;
  let R = 0;
  let G = 0;
  let B = 0;
  const n = d.length / 4;
  for (let k = 0; k < d.length; k += 4) {
    R += d[k];
    G += d[k + 1];
    B += d[k + 2];
  }
  const h2 = (v) => Math.round(v / n).toString(16).padStart(2, "0");
  return `#${h2(R)}${h2(G)}${h2(B)}`;
};

export default renderUploadedFrame;
